// Canvas drawing primitives shared by the Farm ATC renderers (desktop scope and
// phone). Each takes a 2D context plus position/size in whatever units the
// context is currently transformed to, and colors that default to the shared
// theme. No layout or game rules live here.

import { COLORS, MONO } from '../config/theme.js';

const TAU = Math.PI * 2;

/** Faint square grid over a `w` x `h` area starting at (x, y). */
export function drawGrid(ctx, { x = 0, y = 0, w, h, step = 40, color = COLORS.grid, lineWidth = 1 }) {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = lineWidth;
  ctx.beginPath();
  for (let gx = x; gx <= x + w + 0.01; gx += step) { ctx.moveTo(gx, y); ctx.lineTo(gx, y + h); }
  for (let gy = y; gy <= y + h + 0.01; gy += step) { ctx.moveTo(x, gy); ctx.lineTo(x + w, gy); }
  ctx.stroke();
  ctx.restore();
}

/** Dashed field rectangle with its name in the top-left corner. */
export function drawField(ctx, {
  x, y, w, h, label,
  fill = COLORS.fieldFill, stroke = COLORS.fieldStroke, labelColor = COLORS.fieldLabel,
  font = `500 11px ${MONO}`, dash = [4, 4], lineWidth = 1, pad = 7,
}) {
  ctx.save();
  ctx.fillStyle = fill;
  ctx.strokeStyle = stroke;
  ctx.lineWidth = lineWidth;
  ctx.setLineDash(dash);
  ctx.beginPath();
  ctx.rect(x, y, w, h);
  ctx.fill();
  ctx.stroke();
  ctx.setLineDash([]);
  if (label) {
    ctx.fillStyle = labelColor;
    ctx.font = font;
    ctx.textBaseline = 'top';
    ctx.textAlign = 'left';
    ctx.fillText(label.toUpperCase(), x + pad + 1, y + pad);
  }
  ctx.restore();
}

/** Hub: filled ring with a center cross and its name underneath. */
export function drawHub(ctx, {
  x, y, r, label,
  color = COLORS.wheat, fill = COLORS.hubFill, labelColor = COLORS.hubLabel,
  font = `600 11px ${MONO}`, lineWidth = 1.4, glow = 0,
}) {
  ctx.save();
  if (glow > 0) {
    ctx.shadowColor = color;
    ctx.shadowBlur = glow;
  }
  ctx.beginPath();
  ctx.arc(x, y, r, 0, TAU);
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.strokeStyle = color;
  ctx.lineWidth = lineWidth;
  ctx.stroke();
  ctx.shadowBlur = 0;
  ctx.beginPath();
  ctx.moveTo(x - r * 0.5, y); ctx.lineTo(x + r * 0.5, y);
  ctx.moveTo(x, y - r * 0.5); ctx.lineTo(x, y + r * 0.5);
  ctx.stroke();
  if (label) {
    ctx.fillStyle = labelColor;
    ctx.font = font;
    ctx.textBaseline = 'top';
    ctx.textAlign = 'center';
    ctx.fillText(label.toUpperCase(), x, y + r + 4);
  }
  ctx.restore();
}

/**
 * Aircraft blip: a filled dot of radius `r`. With a `heading` (radians,
 * 0 = east, screen y down) it gets a radar-style leader line of length
 * `leader` (default 2.4 r) out of its edge, so you can read where it's going.
 */
export function drawBlip(ctx, {
  x, y, r = 4.2, color, heading = null, leader = null, stroke = null, lineWidth = 1.2,
}) {
  ctx.save();
  if (heading != null) {
    const nx = Math.cos(heading);
    const ny = Math.sin(heading);
    const len = leader ?? r * 2.4;
    ctx.beginPath();
    ctx.moveTo(x + nx * r, y + ny * r);
    ctx.lineTo(x + nx * (r + len), y + ny * (r + len));
    ctx.strokeStyle = color;
    ctx.lineWidth = Math.max(1.4, r * 0.32);
    ctx.lineCap = 'round';
    ctx.stroke();
  }
  ctx.beginPath();
  ctx.arc(x, y, r, 0, TAU);
  ctx.fillStyle = color;
  ctx.fill();
  if (stroke) {
    ctx.strokeStyle = stroke;
    ctx.lineWidth = lineWidth;
    ctx.stroke();
  }
  ctx.restore();
}

/** Callsign label up and to the right of a blip at (x, y). */
export function drawCallsign(ctx, {
  x, y, text, color = COLORS.label, font = `500 10px ${MONO}`, dx = 7, dy = -4,
}) {
  ctx.save();
  ctx.font = font;
  ctx.fillStyle = color;
  ctx.textBaseline = 'bottom';
  ctx.textAlign = 'left';
  ctx.fillText(text, x + dx, y + dy);
  ctx.restore();
}

/** Separation warning ring around (x, y); `pulse` (0..1) breathes the radius. */
export function drawWarningRing(ctx, {
  x, y, r, color = COLORS.alert, lineWidth = 1.6, pulse = 0, dash = null,
}) {
  ctx.save();
  ctx.beginPath();
  ctx.arc(x, y, r + pulse * r * 0.18, 0, TAU);
  ctx.strokeStyle = color;
  ctx.lineWidth = lineWidth;
  if (dash) ctx.setLineDash(dash);
  ctx.stroke();
  ctx.restore();
}

/** Expanding ring, for one-off effects (a landing, an incident). `t` is 0..1. */
export function drawBurst(ctx, { x, y, r0, r1, t, color = COLORS.alertSolid, lineWidth = 2 }) {
  const k = Math.max(0, Math.min(1, t));
  ctx.save();
  ctx.globalAlpha = 1 - k;
  ctx.beginPath();
  ctx.arc(x, y, r0 + (r1 - r0) * k, 0, TAU);
  ctx.strokeStyle = color;
  ctx.lineWidth = lineWidth;
  ctx.stroke();
  ctx.restore();
}
